/** SLA rollup helper for bundles.
 *  Parses a heterogeneous `sla_target` string ("3 business days", "24-48h",
 *  "Next business day", "2 business days") into a comparable day count and
 *  returns the "longest" service SLA — what the whole bundle actually takes. */

const HOUR_TO_DAY = (h) => Math.max(1, Math.ceil(h / 24));

/** Best-effort parse — returns { days, label } or null if unparseable. */
function parseSLA(raw) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim().toLowerCase();
  // Hours: "24-48h" | "24h" | "24-48 hours"
  const hoursMatch = s.match(/(\d+)\s*[-\u2013]\s*(\d+)\s*(?:h|hr|hour)/) || s.match(/(\d+)\s*(?:h|hr|hour)/);
  if (hoursMatch) {
    const hi = Number(hoursMatch[2] || hoursMatch[1]);
    if (!Number.isNaN(hi)) return { days: HOUR_TO_DAY(hi), label: raw };
  }
  // "Same day"
  if (/same[\s-]?day/.test(s)) return { days: 1, label: raw };
  // "Next business day"
  if (/next[\s-]?business[\s-]?day/.test(s)) return { days: 1, label: raw };
  // "Last working day"
  if (/last[\s-]?working[\s-]?day/.test(s)) return { days: 1, label: raw };
  // Days: "3 business days" | "5-7 business days" | "10 days"
  const daysMatch = s.match(/(\d+)\s*[-\u2013]\s*(\d+)\s*(?:business[\s-]?)?days?/) || s.match(/(\d+)\s*(?:business[\s-]?)?days?/);
  if (daysMatch) {
    const hi = Number(daysMatch[2] || daysMatch[1]);
    if (!Number.isNaN(hi)) return { days: hi, label: raw };
  }
  return null;
}

/**
 * Given a list of service objects (each with an optional `sla_target`),
 * return the "longest" SLA — the tightest customer promise across the
 * bundle. Returns { days, label, count } or null if nothing was parseable.
 */
export function rollupSLA(services) {
  if (!Array.isArray(services) || services.length === 0) return null;
  let best = null;
  let count = 0;
  for (const s of services) {
    const parsed = parseSLA(s?.sla_target);
    if (parsed) {
      count += 1;
      if (best === null || parsed.days > best.days) best = parsed;
    }
  }
  if (!best) return null;
  return { ...best, count };
}
